/**
 * Wire types for the Connaro Core API.
 *
 * Written by hand rather than inferred from the server package: the Console is
 * a client of a versioned HTTP contract (§46, §74), not a consumer of internal
 * types. Sharing types directly would let an internal refactor silently change
 * the browser's expectations without anyone noticing the API had changed.
 */

export type ErrorCode =
  | 'VALIDATION_FAILED'
  | 'MALFORMED_REQUEST'
  | 'UNAUTHENTICATED'
  | 'SESSION_EXPIRED'
  | 'INVALID_CREDENTIALS'
  | 'FORBIDDEN'
  | 'PERMISSION_DENIED'
  | 'TENANT_ACCESS_DENIED'
  | 'EMAIL_NOT_VERIFIED'
  | 'REGISTRATION_CLOSED'
  | 'NO_ACTIVE_WORKSPACE'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'ALREADY_EXISTS'
  | 'RATE_LIMITED'
  | 'ENTITLEMENT_LIMIT_REACHED'
  | 'ENTITLEMENT_REQUIRED'
  | 'PLATFORM_REAUTH_REQUIRED'
  | 'PLATFORM_UNAVAILABLE'
  | 'PLATFORM_NOT_SUPPORTED'
  | 'MODERATION_ACTION_UNAVAILABLE'
  | 'CHAT_SEND_UNSUPPORTED'
  | 'CHAT_SEND_UNAVAILABLE'
  | 'INTERNAL_ERROR'
  | 'DEPENDENCY_UNAVAILABLE';

export type FieldError = { field: string; message: string };

export type TenantRole = 'OWNER' | 'ADMIN' | 'OPERATOR' | 'VIEWER';

export type Permission =
  | 'tenant.manage'
  | 'members.manage'
  | 'platforms.view'
  | 'platforms.connect'
  | 'platforms.disconnect'
  | 'chat.view'
  | 'chat.search'
  | 'chat.send'
  | 'moderation.view'
  | 'moderation.resolve'
  | 'moderation.act'
  | 'moderation.rules.manage'
  | 'moderation.notes.view'
  | 'moderation.notes.manage'
  | 'alerts.view'
  | 'alerts.manage'
  | 'bot.view'
  | 'bot.manage'
  | 'bot.commands.manage'
  | 'bot.timers.manage'
  | 'bot.responses.manage'
  | 'bot.test'
  | 'bot.logs.view'
  | 'widgets.view'
  | 'widgets.manage'
  | 'widgets.tokens.rotate'
  | 'billing.view'
  | 'billing.manage'
  | 'settings.view'
  | 'settings.manage';

export type User = {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  createdAt: string;
};

export type Workspace = {
  id: string;
  name: string;
  slug: string;
  role: TenantRole;
};

export type Me = {
  user: User;
  activeWorkspaceId: string | null;
  role: TenantRole | null;
  permissions: Permission[];
  workspaces: Workspace[];
};

export type WorkspaceSettings = {
  workspace: { id: string; name: string; slug: string };
  timezone: string;
  permissions: Permission[];
  role: TenantRole;
};

export type Member = {
  membershipId: string;
  userId: string;
  role: TenantRole;
  status: 'ACTIVE' | 'SUSPENDED';
  email: string;
  displayName: string;
};

export type PlatformName =
  | 'twitch'
  | 'youtube'
  | 'kick'
  | 'blaze'
  | 'discord'
  | 'facebook'
  | 'x'
  | 'tiktok'
  | 'rumble'
  | 'instagram';

export type IntegrationState = 'available' | 'in_progress' | 'planned' | 'blocked';

export type PlatformConnectionStatus =
  | 'PENDING'
  | 'CONNECTING'
  | 'ACTIVE'
  | 'DEGRADED'
  | 'REAUTH_REQUIRED'
  | 'RATE_LIMITED'
  | 'DISCONNECTED'
  | 'ERROR';

export type ConnectionEntry = {
  platform: PlatformName;
  displayName: string;
  integration: IntegrationState;
  summary: string;
  note: string;
  /** Server-computed: the adapter exists AND the actor may connect it. */
  connectable: boolean;
  /**
   * Why the platform cannot be connected, when it cannot.
   *
   * Deliberately separate from `connectable`, which folds the platform, the
   * deployment and the actor's permission into one boolean and so cannot say
   * which of them is missing. This axis never depends on the actor.
   *
   *   unsupported  no adapter exists — nothing an operator can do
   *   needs_setup  built, but this deployment has no credentials for it
   *   ready        built and configured
   */
  availability: 'unsupported' | 'needs_setup' | 'ready';
  /**
   * Server-computed: a connection exists AND the actor may disconnect it.
   *
   * Not the inverse of `connectable`. A workspace whose platform credentials
   * were removed from the deployment can no longer connect, but must still be
   * able to let go of what it already holds.
   */
  disconnectable: boolean;
  connection: {
    id: string;
    status: PlatformConnectionStatus;
    label: string;
    tone: 'ok' | 'warn' | 'error' | 'idle';
    lastConnectedAt: string | null;
    lastEventAt: string | null;
    reconnectCount: number;
    lastErrorMessage: string | null;
    account: {
      username: string | null;
      displayName: string | null;
      avatarUrl: string | null;
    } | null;
  } | null;
};

// --- Connaro events (ARCHITECTURE.md §19) ----------------------------------

export type Badge = {
  id: string;
  label?: string;
  version?: string;
};

export type Actor = {
  platformUserId?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  badges?: Badge[];
};

export type ConnaroEventType =
  | 'chat.message'
  | 'chat.deleted'
  | 'user.joined'
  | 'follow'
  | 'subscription'
  | 'membership'
  | 'gift'
  | 'donation'
  | 'raid'
  | 'system';

/**
 * A normalized event as it arrives over the WebSocket.
 *
 * Restated here rather than imported from `@connaro/platform-core` for the
 * reason at the top of this file: the Console consumes a versioned wire
 * contract, not the server's internal types.
 */
export type ConnaroEvent = {
  id: string;
  tenantId: string;
  platform: PlatformName;
  platformConnectionId: string;
  channelId?: string;
  type: ConnaroEventType;
  occurredAt: string;
  receivedAt: string;
  actor?: Actor;
  message?: { id?: string; text?: string };
  monetary?: { amount?: number; currency?: string; displayAmount?: string };
  metadata?: Record<string, unknown>;
};

export type RetentionSummary = {
  enabled: boolean;
  /** Null means unlimited (Infinity is not valid JSON). */
  days: number | null;
  plan: string;
  trialActive: boolean;
  oldestRetainedAt: string | null;
};

export type ChatHistory = {
  messages: ConnaroEvent[];
  retention: RetentionSummary;
};

// --- sending chat (Connaro Chat) --------------------------------------------

/**
 * Why a connection can or cannot be sent to.
 *
 * Chat's own vocabulary, deliberately smaller than moderation's above: no
 * adapter gates SENDING behind a provider review, so there is no state for it
 * here. Restated from `ChatCapabilityState` in platform-core for the reason at
 * the top of this file — the Console consumes a versioned wire contract, not
 * the server's internal types.
 */
export type ChatCapabilityState =
  'SUPPORTED' | 'UNSUPPORTED' | 'NOT_IMPLEMENTED' | 'PERMISSION_REQUIRED' | 'REAUTH_REQUIRED';

/**
 * What one platform enforces on an outbound message, as the server reports it.
 *
 * TWO INDEPENDENT AXES, and the second is not decoration. Kick caps a message
 * at 500 grapheme clusters AND, separately, at 2048 UTF-8 bytes; a message can
 * pass either and fail the other, so a counter that shows one number for Kick
 * is telling the operator something that is not true.
 *
 * The figures are never compiled into this bundle. They come from `GET
 * /chat/capabilities`, which reads the same `CHAT_LIMITS` the API enforces on
 * the way in — a copy here would be a claim about somebody else's product that
 * nothing keeps current.
 */
export type ChatLengthLimit = {
  graphemes: number;
  utf8Bytes?: number;
};

/**
 * One connection's answer to "can this workspace speak here, and if not, why?"
 *
 * `note` is the ADAPTER's sentence, resolved server-side from the scopes the
 * provider actually granted. §65 requires that anything the Console cannot
 * offer says why; this is that reason, and the Console renders it rather than
 * translating a state name into a guess of its own.
 */
export type ChatSendCapability = {
  platform: PlatformName;
  platformConnectionId: string;
  displayName: string;
  status: PlatformConnectionStatus;
  state: ChatCapabilityState;
  note: string;
  /**
   * The sentence that goes in FRONT of the note, or null.
   *
   * The half a per-platform note cannot say — that Connaro will not be doing
   * this — and it arrives written rather than assembled here. A browser
   * composing it from the state name would be authoring a claim about server
   * behaviour, and the send route already writes this exact sentence when it
   * refuses. Null where the note already opens with the remedy.
   */
  lead: string | null;
  /** Null where the platform has no write path Connaro can use at all. */
  limit: ChatLengthLimit | null;
};

/**
 * Where a message may go, for one connection.
 *
 * `requiresChannel` is computed BY THE SERVER — a Discord connection covers a
 * whole guild, so the send route makes the operator name a channel and refuses
 * one for every other platform. The Console must never re-derive that from the
 * platform name: the send route rejects a stray `channelId` rather than
 * ignoring it, so a second copy of the rule turns into rejected requests the
 * moment either copy moves.
 */
export type ChatSendConnectionTargets = {
  platformConnectionId: string;
  requiresChannel: boolean;
  channels: { id: string; name: string | null }[];
};

/**
 * What became of a send, on ONE platform. THREE outcomes, and the third is the
 * point.
 *
 * UNKNOWN means Connaro passed the message on and cannot confirm what happened
 * to it — the worker did not answer in time, or died mid-send. A fan-out
 * containing one arrives as HTTP 202, which every `response.ok` check in the
 * world reads as success. It is not success: an operator told a message sent
 * when Connaro does not know will not check, and one told it failed will send
 * it again and the audience will see it twice. Branch on this field, never on
 * the status code.
 */
export type ChatSendStatus = 'SENT' | 'UNKNOWN' | 'FAILED';

export type ChatSendTargetResult = {
  platformConnectionId: string;
  platform: PlatformName;
  displayName: string;
  status: ChatSendStatus;
  /** Operator-facing, and UNTRUSTED: render through React text nodes (§21). */
  reason?: string;
};

/**
 * The body of a send, which is a LIST because a send can go to several
 * platforms at once.
 *
 * THERE IS NO AGGREGATE STATUS FIELD, and there must not be one. A single word
 * for a mixed outcome is wrong about at least one platform, and a Console
 * rendering it beside the per-target rows would be contradicting itself. The
 * HTTP code carries a summary for callers with nothing else to read; this is
 * where the truth lives.
 */
export type ChatSendResults = {
  results: ChatSendTargetResult[];
};

// --- Connaro Moderator (§98) ------------------------------------------------

export type ModerationSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type FindingState = 'OPEN' | 'ACKNOWLEDGED' | 'ACTIONED' | 'DISMISSED' | 'EXPIRED';

export type ModerationRuleKind = 'blocked_terms' | 'regex' | 'links' | 'repetition' | 'flood';

export type ModerationActionType = 'message.delete' | 'user.timeout' | 'user.ban' | 'user.unban';

export type ModerationCapabilityState =
  | 'SUPPORTED'
  | 'UNSUPPORTED'
  | 'NOT_IMPLEMENTED'
  | 'PERMISSION_REQUIRED'
  | 'REAUTH_REQUIRED'
  | 'PROVIDER_REVIEW_REQUIRED';

export type ModerationCapability = {
  action: ModerationActionType;
  state: ModerationCapabilityState;
  /** Operator-facing explanation, written by the adapter that owns the fact. */
  note: string;
  missingScopes?: string[];
};

export type ModerationRule = {
  id: string;
  name: string;
  kind: ModerationRuleKind;
  enabled: boolean;
  severity: ModerationSeverity;
  platforms: PlatformName[];
  channelIds: string[];
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type ModerationFinding = {
  id: string;
  ruleId: string | null;
  ruleName: string;
  severity: ModerationSeverity;
  state: FindingState;
  platform: PlatformName;
  platformConnectionId: string;
  channelId: string | null;
  /** The ConnaroEvent id, so a finding can be matched to its chat row. */
  eventId: string;
  platformMessageId: string | null;
  platformUserId: string | null;
  actorUsername: string | null;
  actorDisplayName: string | null;
  /**
   * UNTRUSTED user-generated content.
   *
   * Rendered through React's text interpolation only. Nothing in the Console
   * may put this near `dangerouslySetInnerHTML` (§21: XSS-safe rendering, no
   * HTML execution).
   */
  messageText: string | null;
  reason: string;
  matchedText: string | null;
  occurredAt: string;
  createdAt: string;
  resolvedAt: string | null;
};

export type ModerationAction = {
  id: string;
  findingId: string | null;
  platform: PlatformName;
  actionType: ModerationActionType;
  status: 'SUCCEEDED' | 'FAILED';
  durationSeconds: number | null;
  reason: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  createdAt: string;
};

export type ModerationActorNote = {
  id: string;
  platform: PlatformName;
  platformUserId: string;
  actorDisplayName: string | null;
  note: string;
  createdAt: string;
  updatedAt: string;
};

export type ModerationFindingDetail = {
  finding: ModerationFinding;
  history: ModerationFinding[];
  notes: ModerationActorNote[];
  actions: ModerationAction[];
  capabilities: ModerationCapability[];
};

export type ModerationPlatformCapabilities = {
  platform: PlatformName;
  platformConnectionId: string;
  status: string;
  actions: ModerationCapability[];
};

/**
 * Per-person Console preferences (§22).
 *
 * Mirrors `chatPreferencesSchema` in platform-core. The API validates and
 * clamps every value, so the Console can treat what it receives as complete —
 * but the DEFAULTS live here too, because they are needed before the first
 * response arrives and if it never does.
 */
export type TimestampFormat = '24h_seconds' | '24h' | '12h_seconds' | '12h' | 'hidden';

/** What the feed shows of stored history. NOT the same as retention (§31). */
export type ChatHistoryMode = 'all' | 'live_only' | 'off';

/** Must match a `[data-theme]` block in tokens.css. */
export type ConsoleTheme = 'dark' | 'light' | 'twitch';

export type ChatPreferences = {
  fontSize: number;
  timestampFormat: TimestampFormat;
  showPlatformIcons: boolean;
  showProfilePictures: boolean;
  highlightMentions: boolean;
  alternatingBackgrounds: boolean;
  showQuickActions: boolean;
  randomNameColors: boolean;
  /** Seconds before a message fades; 0 means never. */
  fadeOutSeconds: number;
  chatHistory: ChatHistoryMode;
  theme: ConsoleTheme;
  sevenTvEmotes: boolean;
  bttvEmotes: boolean;
  ffzEmotes: boolean;
  sevenTvPaints: boolean;
  /** Event types switched off. Absent means shown. */
  hiddenEventTypes: string[];
  emoteSize: number;
  dimChatHistory: boolean;
};

export const DEFAULT_CHAT_PREFERENCES: ChatPreferences = {
  fontSize: 14,
  timestampFormat: '24h_seconds',
  showPlatformIcons: true,
  showProfilePictures: true,
  highlightMentions: true,
  alternatingBackgrounds: false,
  showQuickActions: false,
  randomNameColors: false,
  fadeOutSeconds: 0,
  chatHistory: 'all',
  theme: 'dark',
  sevenTvEmotes: false,
  bttvEmotes: false,
  ffzEmotes: false,
  sevenTvPaints: false,
  hiddenEventTypes: [],
  emoteSize: 28,
  dimChatHistory: true,
};

// --- Connaro Alerts (§99, §100; Alerts brief §5-§14) ------------------------

/**
 * What `GET /api/v1/alerts/triggers` offers, straight from the §99 capability
 * matrix (`PLATFORM_EVENT_CAPABILITIES` in the API).
 *
 * The trigger picker below is driven ENTIRELY by this response. There is no
 * hardcoded list of triggers or platforms anywhere in this page — that would
 * be the second table §99 exists to prevent, drifting the moment an adapter
 * gains or loses a capability.
 */
export type AlertTrigger = {
  eventType: ConnaroEventType;
  /** Never empty: the server filters out any trigger nothing can emit. */
  platforms: PlatformName[];
};

export type AlertCondition = Record<string, unknown>;

export type AlertDefinition = {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  eventType: ConnaroEventType;
  /** Empty means "any platform that can trigger this event type". */
  platforms: PlatformName[];
  conditions: AlertCondition[];
  template: string;
  durationMs: number;
  cooldownSeconds: number;
  /**
   * Appearance, as `AlertStyle` (`packages/alerts/src/style.ts`) reads it —
   * untrusted JSON at rest, bounded on the way into `AlertRender` by
   * `pickEnum` (`./alerts/style-values.ts`), never trusted here.
   *
   * OPTIONAL, unlike the column itself: an older API server, from before
   * this field existed on the wire, sends a `definition` without it. Every
   * reader falls back to `{}`, the same way `queue-client.ts`'s
   * `PendingAlertsResponse.layout` does for the overlay.
   */
  style?: Record<string, unknown> | undefined;
  createdAt: string;
  updatedAt: string;
};

export type AlertOutput = {
  id: string;
  name: string;
  enabled: boolean;
  graceSeconds: number;
  /**
   * Where this output's alerts sit, as `AlertLayout` reads it. Same trust
   * rules as `style` above, and OPTIONAL for the same version-skew reason.
   */
  layout?: Record<string, unknown> | undefined;
  createdAt: string;
};

/**
 * A minted output render credential, as the LIST/nested shape ("§ record")
 * looks once created — minus its plaintext.
 */
export type AlertOutputToken = {
  id: string;
  label: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
};

/**
 * The response of `POST /api/v1/alerts/outputs/:outputId/tokens`.
 *
 * DELIBERATELY FLAT — `{ token, id, label, ... }` — unlike every sibling
 * Alerts route, which nests its payload (`{ definition }`, `{ output }`).
 * This is not a bug to "fix" into a nested shape; it is the real wire shape
 * (`apps/api/src/routes/alerts.ts`: `reply.send({ token, ...view })`), and the
 * plaintext `token` field exists on THIS response only — no other Alerts
 * route ever returns it, because nothing stores it in recoverable form.
 */
export type AlertOutputTokenCreated = AlertOutputToken & { token: string };

export const ALERT_STATUSES = ['QUEUED', 'ACTIVE', 'COMPLETED', 'EXPIRED', 'FAILED'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

/**
 * The response of `POST /api/v1/alerts/definitions/:id/test`.
 *
 * A NARROWER view than `AlertInstanceHistoryView` below — this is the same
 * shape the overlay itself reads, deliberately withholding which definition
 * or output it belongs to. The Console only needs to know the render
 * succeeded and say so; the definition and output are already on screen.
 */
export type AlertTestResult = {
  id: string;
  status: AlertStatus;
  text: string;
  durationMs: number;
  queuedAt: string;
  synthetic: boolean;
};

/**
 * A history entry, for the operator's own Console rather than the overlay.
 *
 * `text` is untrusted, chat-derived content (§21, §57): rendered through
 * React text interpolation only, never `dangerouslySetInnerHTML`.
 */
export type AlertInstanceHistory = {
  id: string;
  definitionId: string;
  outputId: string;
  platform: PlatformName;
  status: AlertStatus;
  synthetic: boolean;
  text: string;
  durationMs: number;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  errorCode: string | null;
};

/** Which service an emote came from; each has its own id and size rules. */
export type EmoteProvider = 'seventv' | 'bttv' | 'ffz';

/** Emote name to the emote it resolves to, served by `GET /emotes`. */
export type EmoteMap = Record<string, { provider: EmoteProvider; id: string }>;

/**
 * A name paint, as CSS CONNARO BUILT from 7TV's numeric fields.
 *
 * Never a style string 7TV supplied: the API returns typed layers with numeric
 * angles and RGBA integers, and the server assembles these two declarations
 * from those numbers. The Console sets exactly these two properties.
 */
export type NamePaint = {
  id: string;
  name: string;
  backgroundImage: string;
  textShadow?: string | undefined;
};

// --- Connaro Bot (§102) -----------------------------------------------------

export type BotAudience = 'everyone' | 'subscriber' | 'moderator' | 'broadcaster';
export type BotTargetMode = 'origin' | 'selected' | 'all';
export type BotResponseMode = 'static' | 'random';
export type BotRotationMode = 'sequential' | 'random';
export type BotTriggerKind = 'exact' | 'starts_with' | 'contains';

export type BotExecutionOutcome =
  | 'DISPATCHED'
  | 'COOLDOWN'
  | 'ROLE_DENIED'
  | 'ROLE_UNAVAILABLE'
  | 'DISABLED'
  | 'NO_TARGET'
  | 'LOOP_PREVENTED'
  | 'NOT_ENTITLED'
  | 'ERROR';

/**
 * One platform's row on the Bot settings surface.
 *
 * `botEnabled` is the OPERATOR's switch and says nothing about whether the
 * provider will accept a send — §102 forbids conflating them, and the Console
 * must render two facts because there are two (`connectionStatus` is the
 * other, and capability is resolved per execution).
 */
export type BotPlatformSetting = {
  platform: PlatformName;
  displayName: string;
  connectionStatus: PlatformConnectionStatus;
  botEnabled: boolean;
};

export type BotSettings = {
  enabled: boolean;
  commandPrefix: string;
  platforms: BotPlatformSetting[];
};

export type BotCommand = {
  id: string;
  /** Null only if a command somehow has no primary trigger, which is unrepresentable. */
  trigger: string | null;
  aliases: string[];
  responses: string[];
  enabled: boolean;
  description: string | null;
  audience: BotAudience;
  targetMode: BotTargetMode;
  targetPlatforms: PlatformName[];
  responseMode: BotResponseMode;
  globalCooldownSeconds: number;
  userCooldownSeconds: number;
  lastUsedAt: string | null;
  useCount: number;
  updatedAt: string;
};

export type BotTimer = {
  id: string;
  name: string;
  enabled: boolean;
  intervalSeconds: number;
  minMessages: number | null;
  liveOnly: boolean;
  rotation: BotRotationMode;
  targetMode: BotTargetMode;
  targetPlatforms: PlatformName[];
  messages: string[];
  lastExecutedAt: string | null;
  nextEligibleAt: string | null;
  updatedAt: string;
};

export type BotResponseRule = {
  id: string;
  name: string;
  enabled: boolean;
  kind: BotTriggerKind;
  triggerValue: string;
  body: string;
  cooldownSeconds: number;
  targetMode: BotTargetMode;
  targetPlatforms: PlatformName[];
  updatedAt: string;
};

/** One row of the execution log. `simulated` is never hidden by default (§51). */
export type BotExecution = {
  id: string;
  occurredAt: string;
  kind: 'command' | 'timer' | 'response_rule';
  sourceId: string | null;
  sourceLabel: string;
  platform: PlatformName | null;
  actorDisplayName: string | null;
  matchedTrigger: string | null;
  outcome: BotExecutionOutcome;
  reason: string | null;
  renderedPreview: string | null;
  targets: unknown;
  simulated: boolean;
};

/**
 * What a simulation answers.
 *
 * `providerSend` is a fixed string the SERVER writes, and the Console renders
 * it rather than composing its own disclaimer — a sentence about what the
 * server did not do is the server's to write, the same rule the chat
 * composer's `lead` follows.
 */
export type BotTestResult = {
  simulated: true;
  providerSend?: string;
  stages: { name: string; passed: boolean; detail: string }[];
  targets: {
    platform: PlatformName;
    displayName: string;
    eligible: boolean;
    skipped: string | null;
    capability: string;
    note: string | null;
  }[];
  renderedResponse: string | null;
  cooldownNamespace?: string[];
};

export type BotVariable = { name: string; description: string };
